import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { AccountInfoLabel } from '@woovi/ui';
import { convertToMoney } from '@woovi/utils';

import type { PaymentLinkCompletedPixCreditCard_charge$key } from './__generated__/PaymentLinkCompletedPixCreditCard_charge.graphql.ts';
import type { PaymentLinkCompletedPixCreditCard_query$key } from './__generated__/PaymentLinkCompletedPixCreditCard_query.graphql.ts';
import { useChargePaymentMethod } from '../../../charge/useChargePaymentMethod.tsx';
import { useGraphQLType } from '../../../graphql/useGraphQLType.ts';

import type { ReactNode } from 'react';

type PaymentLinkCompletedPixCreditCardProps = {
  query: PaymentLinkCompletedPixCreditCard_query$key;
  charge: PaymentLinkCompletedPixCreditCard_charge$key;
};
const PaymentLinkCompletedPixCreditCard = (
  props: PaymentLinkCompletedPixCreditCardProps,
): ReactNode => {
  const { t } = useTranslation();

  const query = useFragment<PaymentLinkCompletedPixCreditCard_query$key>(
    graphql`
      fragment PaymentLinkCompletedPixCreditCard_query on Query {
        ...useGraphQLType_query
      }
    `,
    props.query,
  );

  const charge = useFragment<PaymentLinkCompletedPixCreditCard_charge$key>(
    graphql`
      fragment PaymentLinkCompletedPixCreditCard_charge on Charge {
        value
        valueWithDiscount
        ...useChargePaymentMethod_charge
      }
    `,
    props.charge,
  );

  const { isGraphQLMain } = useGraphQLType(query);
  const { isPixCreditCard, pixCob, creditCard } =
    useChargePaymentMethod(charge);

  if (!isPixCreditCard) {
    return null;
  }

  if (isGraphQLMain) {
    return null;
  }

  const isOnlyPix = !creditCard?.method;

  if (isOnlyPix) {
    return null;
  }

  return (
    <Stack spacing={2} sx={{ mt: '40px' }}>
      <AccountInfoLabel
        label={t('Pix Entrance')}
        text={convertToMoney(pixCob.valueWithDiscount)}
      />
      <AccountInfoLabel
        label={t('Credit Card Installments')}
        text={`${creditCard.installmentNumber}x ${convertToMoney(
          creditCard.value,
        )}`}
      />
    </Stack>
  );
};

export default PaymentLinkCompletedPixCreditCard;
